/**
 * StarRating - Displays star rating using the existing Star icon component
 * Uses inline font-size for reliable sizing (Star SVG uses 1em)
 */

import type { FC } from "react";
import Star from "~/components/icons/Star";
import classNames from "classnames";

interface StarRatingProps {
  rating: number;
  /** Predefined size or custom pixel value */
  size?: "sm" | "md" | "lg" | "xl" | number;
  showValue?: boolean;
  className?: string;
}

/**
 * Size in pixels for each preset
 * Star SVG uses height="1em" so fontSize controls the size
 */
const sizePx: Record<string, number> = {
  sm: 14,
  md: 20,
  lg: 22,
  xl: 28,
};

export const StarRating: FC<StarRatingProps> = ({
  rating,
  size = "md",
  showValue = false,
  className,
}) => {
  // Round rating to nearest 0.5 step for display:
  // 0.00–0.49 → 0 | 0.50–0.74 → 0.5 | 0.75–1.24 → 1 | 1.25–1.74 → 1.5 | etc.
  const displayRating = Math.min(5, rating < 0.5 ? 0 : Math.round(rating * 2) / 2);
  const fullStars = Math.floor(displayRating);
  const hasHalfStar = displayRating % 1 === 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  // Get pixel size from preset or use custom number
  const pxSize = typeof size === "number" ? size : sizePx[size] || 20;

  return (
    <div className={classNames("d-flex align-items-center", className)}>
      <span
        className="d-inline-flex"
        style={{ fontSize: `${pxSize}px` }}
      >
        {[...Array(fullStars)].map((_, i) => (
          <Star key={`full-${i}`} mode="full" />
        ))}
        {hasHalfStar && <Star mode="half" />}
        {[...Array(emptyStars)].map((_, i) => (
          <Star key={`empty-${i}`} mode="empty" />
        ))}
      </span>
      {showValue && (
        <span className="ml-2 font-weight-bold text-darkblue">
          {rating.toFixed(1)}
        </span>
      )}
    </div>
  );
};

export default StarRating;
